import { Fragment, type FunctionComponent, type MouseEvent } from 'react';
import {
    GAEventName,
    GAEventTrigger,
    GAFlowName,
    useGoogleAnalytics,
} from '../../../utils/googleAnalytics4AssetOnboardingFlow';
import { FormStepNavigation } from './EasyOnboardingDialog';
import SelectionTile from './SelectionTile';
import { ALL_ASSET_TYPE_TILES } from './configuration/assetTypeTileConfig';
import type { AssetTypeTile } from './configuration/types';
import { AssetType } from '../types';
import MaintenanceState from '@rio-cloud/rio-uikit/MaintenanceState';
import { useIntl } from 'react-intl';
import { useIsAssetAdministrationInMaintenanceToggle } from '../../../../../configuration/featureToggle/toggleHooks.ts';

interface FormStepSelectAssetTypeProps {
    setSelectedAssetType: (value: AssetTypeTile) => void;
    handleFormStepNavigation: (goto: FormStepNavigation, e?: MouseEvent) => void;
    isLatamTenant?: boolean;
}

export const FormStepSelectAssetType: FunctionComponent<FormStepSelectAssetTypeProps> = (
    props: FormStepSelectAssetTypeProps
) => {
    const { setSelectedAssetType, handleFormStepNavigation } = props;
    const { dataLayerPush } = useGoogleAnalytics();
    const intl = useIntl();

    const types = props.isLatamTenant
        ? ALL_ASSET_TYPE_TILES
        : ALL_ASSET_TYPE_TILES.filter(value => value.assetType !== AssetType.car);

    const isAssetAdminInMaintenance = useIsAssetAdministrationInMaintenanceToggle();

    return (
        <>
            <div
                className={`display-grid grid-cols-2 ${types.length > 4 ? 'grid-cols-3-sm' : ''} gap-10-xs gap-20 padding-20 position-relative`}
            >
                {types.map((item, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: suppress out of convenience during migration to biome
                    <Fragment key={item.assetType + index}>
                        <SelectionTile
                            show='image'
                            name={item.assetType}
                            autoTrackingKeyPrefix='easyOnboarding.dialog.choose.asset'
                            image={item.logo}
                            animationDelay={index * 0.1}
                            tileType='default'
                            data-cy={`selection-tile-${item.assetType}`}
                            onClick={e => {
                                dataLayerPush({
                                    trigger: GAEventTrigger.click,
                                    event: GAEventName.selectAssetType,
                                    element_name: item.assetType,
                                    flow_name: GAFlowName.easyOnboardingFlow,
                                });
                                setSelectedAssetType(item);
                                handleFormStepNavigation(FormStepNavigation.next, e);
                            }}
                        />
                    </Fragment>
                ))}
            </div>
            {isAssetAdminInMaintenance && (
                <div className={'position-absolute left-0 top-0 padding-100'}>
                    <MaintenanceState
                        headline={intl.formatMessage({ id: 'intl-msg:sharing.maintenance' })}
                        message={intl.formatMessage({
                            id: 'intl-msg:create-new-dialog.telematics.asset-administration-maintenance',
                        })}
                    />
                </div>
            )}
        </>
    );
};
